import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input, LoaderCircle, Radio, RadioGroup } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import RouterLink from '@/components/RouterLink'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { useSearch } from '@/hooks/searchParams'
import type { EmployeeConsentDetails } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { CostObjectHistory } from './CostObjectHistory'
import { EmployeeList } from './EmployeeList'

import adminQueries from '../../queries'

type EligibleCostObjectsType = EmployeeConsentDetails['eligibleCostObjects'][number]

export const EmployeeConsentReport: FC = () => {
  const navigate = useNavigate()
  const params = useParams()
  const consentId = Number(params.consentId)
  const translate = useTranslation()

  const [historyModal, setHistoryModal] = useState<number | null>(null)

  if (isNaN(consentId)) throw Error(translate('Employee consent ID is not a number!'))

  const [queryView, setQueryView] = useSearchParamsByKey('approval-view')

  const { searchFilter, setSearchFilter } = useSearch()

  useEffect(() => {
    if (queryView) {
      setQueryView(queryView)
    } else {
      setQueryView('all')
    }
  }, [queryView, setQueryView])

  const { data, isLoading } = useQuery(adminQueries.adminConsentDetails(consentId), () =>
    adminResources.getConsentDetails(consentId)
  )

  const stringMatcher = (item: EligibleCostObjectsType) => [
    item.name,
    item.code,
    item.department,
    item.employeeNo,
    item.email,
  ]

  const onOpenModal = (id: number) => {
    setHistoryModal(id)
  }

  if (isLoading) return <LoaderCircle />
  if (!data || data.eligibleCostObjects.length === 0) return <NoResults />

  if (!data.eligibleCostObjects) return null

  const isEmployeeVisible = (employee: EligibleCostObjectsType) => {
    if (queryView === 'approved') return employee.hasApproved
    if (queryView === 'not-approved') return !employee.hasApproved && !employee.hasRevoked
    if (queryView === 'revoked') return employee.hasRevoked
    return true
  }

  const employeesFiltered =
    data.eligibleCostObjects?.filter(
      employee =>
        isEmployeeVisible(employee) && getQueryFilter(searchFilter, stringMatcher)(employee)
    ) || []

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="Consent reports for" />
        <RouterLink
          to={APP_ROUTES.ADMIN_EMPLOYEE_CONSENTS}
          hasUnderline={false}
          size="large"
          colorMode="gray"
          className="mb-2 mt-0 block overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {data.text}
        </RouterLink>
      </Container.Header>

      <div className="flex w-full flex-wrap items-center justify-start gap-8">
        <Input
          id="employee-search"
          value={searchFilter}
          placeholder={translate('Search')}
          onChange={e => setSearchFilter(e.target.value)}
        />

        <RadioGroup className="flex">
          <Radio
            id="show-all-consent-approvals"
            label={<Translation id="Show all" />}
            checked={queryView === 'all'}
            onChange={() => setQueryView('all')}
          />

          <Radio
            id="consent-approved"
            label={<Translation id="Show only approved" />}
            checked={queryView === 'approved'}
            onChange={() => setQueryView('approved')}
          />

          <Radio
            id="consent-not-approved"
            label={<Translation id="Show only not approved" />}
            checked={queryView === 'not-approved'}
            onChange={() => setQueryView('not-approved')}
          />

          <Radio
            id="consent-revoked"
            label={<Translation id="Show revoked" />}
            checked={queryView === 'revoked'}
            onChange={() => setQueryView('revoked')}
          />
        </RadioGroup>
      </div>

      <EmployeeList
        employeeList={employeesFiltered}
        consentName={data.text}
        searchFilter={searchFilter}
        openModal={onOpenModal}
      />

      {historyModal && (
        <CostObjectHistory
          consent={data}
          costObject={data.eligibleCostObjects.find(item => item.id === historyModal)}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </Container>
  )
}
